import { useMemo, useRef, useState } from 'react';
import {
  ArrowClockwise,
  FileArrowUp,
  ImageSquare,
  MagnifyingGlass,
  ShieldCheck,
  UploadSimple,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToast } from '@/hooks/use-toast';
import { InsertSnippetModal } from '../../components/insert-snippet-modal';
import { MediaCard } from '../../components/media-card';
import { MediaDropzone } from '../../components/media-dropzone';
import {
  useDeleteMedia,
  useMediaLibrary,
  useUploadMedia,
} from '../../hooks/use-media-library';
import type { MediaAsset, MediaAssetType } from '../../types/media-library.types';
import { formatFileSize, matchesTypeFilter } from '../../utils/media-library.utils';

type UploadQueueItem = {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'done' | 'failed';
  error?: string;
};

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/svg+xml',
  'image/webp',
]);

export const MediaLibraryPage = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<MediaAssetType>('ALL');
  const [simulateServerError, setSimulateServerError] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<MediaAsset | null>(null);
  const [assetToInsert, setAssetToInsert] = useState<MediaAsset | null>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);

  const {
    data: assets = [],
    isLoading,
    isError,
    refetch,
  } = useMediaLibrary({
    search: searchTerm,
    type: typeFilter,
  });

  const { mutateAsync: uploadMedia } = useUploadMedia();
  const { mutateAsync: deleteMedia, isPending: isDeleting } = useDeleteMedia();

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const matchesSearch =
        !searchTerm.trim() ||
        asset.name.toLowerCase().includes(searchTerm.trim().toLowerCase());
      const matchesType = matchesTypeFilter(asset, typeFilter);
      return matchesSearch && matchesType;
    });
  }, [assets, searchTerm, typeFilter]);

  const removeQueueItem = (id: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const handleProcessUpload = async (files: File[]) => {
    if (files.length === 0) return;

    for (const file of files) {
      const queueId = `uprow-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      const newItem: UploadQueueItem = {
        id: queueId,
        name: file.name,
        progress: 15,
        status: 'uploading',
      };

      setUploadQueue((prev) => [newItem, ...prev]);

      // Validation check matching prototype
      if (file.size > MAX_UPLOAD_BYTES) {
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? {
                  ...item,
                  progress: 100,
                  status: 'failed',
                  error: `“${file.name}” is ${formatFileSize(file.size)}. The limit is ${formatFileSize(MAX_UPLOAD_BYTES)}.`,
                }
              : item
          )
        );
        toast({
          variant: 'destructive',
          title: `Could not upload ${file.name}.`,
        });
        continue;
      }

      if (file.type && !ALLOWED_MIME_TYPES.has(file.type)) {
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? {
                  ...item,
                  progress: 100,
                  status: 'failed',
                  error: `“${file.name}” is ${file.type || 'an unrecognized type'}. Allowed types: PNG, JPEG, GIF, SVG, WEBP.`,
                }
              : item
          )
        );
        toast({
          variant: 'destructive',
          title: `Could not upload ${file.name}.`,
        });
        continue;
      }

      // Progress animation
      setUploadQueue((prev) =>
        prev.map((item) => (item.id === queueId ? { ...item, progress: 70 } : item))
      );

      if (simulateServerError) {
        setSimulateServerError(false);
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? {
                  ...item,
                  progress: 100,
                  status: 'failed',
                  error: `Something went wrong uploading “${file.name}”. Try again.`,
                }
              : item
          )
        );
        toast({
          variant: 'destructive',
          title: `Could not upload ${file.name}.`,
        });
        continue;
      }

      try {
        await uploadMedia(file);
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === queueId ? { ...item, progress: 100, status: 'done' } : item
          )
        );
        toast({
          variant: 'success',
          title: `“${file.name}” uploaded.`,
        });

        // Auto-remove finished row after 1.5s
        setTimeout(() => {
          removeQueueItem(queueId);
        }, 1500);
      } catch (uploadErr) {
        const errorMsg =
          uploadErr instanceof Error
            ? uploadErr.message
            : `Something went wrong uploading “${file.name}”. Try again.`;
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? {
                  ...item,
                  progress: 100,
                  status: 'failed',
                  error: errorMsg,
                }
              : item
          )
        );
        toast({
          variant: 'destructive',
          title: `Could not upload ${file.name}.`,
        });
      }
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      void handleProcessUpload(files);
    }
    event.target.value = '';
  };

  const handleConfirmDelete = async () => {
    if (!assetToDelete) return;

    try {
      await deleteMedia(assetToDelete.id);
      toast({
        variant: 'success',
        title: 'File deleted.',
      });
      setAssetToDelete(null);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Could not delete this file.',
      });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setTypeFilter('ALL');
  };

  return (
    <div className="flex min-h-full flex-col gap-5 pb-6">
      {/* EU/CH Residency Notice Banner matching prototype */}
      <div
        className="flex items-start gap-2.5 rounded-[8px] px-3.5 py-2.5"
        style={{ background: '#E6F0F8' }}
      >
        <ShieldCheck
          className="flex-none text-[16px]"
          style={{ color: '#0066B2', marginTop: 2 }}
          weight="regular"
        />
        <p className="text-[12px] leading-[18px]" style={{ color: '#003F6F' }}>
          Uploads are stored in our own EU/CH object store, never a vendor CDN &mdash; media
          is personal-data-adjacent, and residency here is a product commitment.
        </p>
      </div>

      {/* Header section matching prototype */}
      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-[24px] font-semibold leading-8 text-[#1B2021]">
            Media library
          </h1>
          <p className="mt-0.5 text-[14px] text-[#696969]">
            Images you can reuse across templates and campaigns.
          </p>
        </div>

        <div className="flex-1" />

        <Button
          type="button"
          onClick={handleUploadButtonClick}
          className="gap-1.5 rounded-[6px] bg-[#0066B2] px-3.5 py-2 text-[14px] font-medium text-white shadow-none hover:bg-[#005494]"
        >
          <UploadSimple className="h-4 w-4" weight="bold" />
          <span>Upload</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
          multiple
          className="hidden"
          aria-label="Files to upload"
          onChange={handleFileInputChange}
        />
      </div>

      {/* Drop Zone matching prototype */}
      <MediaDropzone onFilesSelected={(files) => void handleProcessUpload(files)} />


      {/* Upload Queue matching prototype */}
      {uploadQueue.length > 0 && (
        <div className="flex flex-col gap-2">
          {uploadQueue.map((item) => (
            <div
              key={item.id}
              className="flex flex-col gap-2 rounded-[8px] border border-[#E7E8EA] bg-white px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <FileArrowUp className="h-4 w-4 flex-none text-[#707070]" />
                <span className="flex-1 truncate text-[14px] font-medium text-[#1B2021]">
                  {item.name}
                </span>
                <span className="text-[12px] text-[#707070]">
                  {item.status === 'uploading'
                    ? 'Uploading…'
                    : item.status === 'done'
                      ? 'Done'
                      : 'Failed'}
                </span>
                <button
                  type="button"
                  onClick={() => removeQueueItem(item.id)}
                  className="rounded p-1 text-[#707070] transition-colors hover:text-[#1B2021]"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="h-2 w-full overflow-hidden rounded-full bg-[#F4F5F6]">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${item.progress}%`,
                    background: item.status === 'failed' ? '#D80032' : '#0066B2',
                  }}
                />
              </div>

              {item.error && (
                <p className="text-[12px]" style={{ color: '#D80032' }}>
                  {item.error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Filter and Search Bar matching prototype */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] max-w-[360px] flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[#707070]" />
          <Input
            id="q"
            type="text"
            placeholder="Search by file name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-[36px] rounded-[6px] border-[#DEE0E3] pl-[34px] text-[13px] text-[#1B2021] placeholder:text-[#707070] focus-visible:ring-[#0066B2]"
            aria-label="Search media by file name"
          />
        </div>

        <Select
          value={typeFilter}
          onValueChange={(val) => setTypeFilter(val as MediaAssetType)}
        >
          <SelectTrigger className="h-[36px] w-auto min-w-[150px] rounded-[6px] border-[#DEE0E3] text-[13px] text-[#1B2021] focus:ring-[#0066B2]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            <SelectItem value="PNG">PNG</SelectItem>
            <SelectItem value="JPEG">JPEG</SelectItem>
            <SelectItem value="GIF">GIF</SelectItem>
            <SelectItem value="SVG">SVG</SelectItem>
            <SelectItem value="WEBP">WEBP</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <span className="text-[12px] text-[#707070]">
          {filteredAssets.length}{' '}
          {filteredAssets.length === 1 ? 'asset' : 'assets'}
        </span>
      </div>

      {/* Grid Content matching prototype */}
      {isLoading ? (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`skel-${i}`}
              className="flex flex-col overflow-hidden rounded-[12px] border border-[#E7E8EA] bg-white"
            >
              <Skeleton className="aspect-[16/10] w-full rounded-none bg-[#F4F5F6]" />
              <div className="flex flex-col gap-2 p-3">
                <Skeleton className="h-3.5 w-3/4 rounded bg-[#E7E8EA]" />
                <Skeleton className="h-3 w-2/5 rounded bg-[#F4F5F6]" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="mx-auto flex max-w-[380px] flex-col items-center gap-2.5 py-14 text-center">
          <WarningCircle className="h-7 w-7 text-[#D80032]" weight="regular" />
          <div className="text-[18px] font-semibold text-[#1B2021]">
            Could not load media
          </div>
          <p className="text-[14px] text-[#696969]">
            Something went wrong while fetching your media library. Check your connection and
            try again.
          </p>
          <Button
            type="button"
            onClick={() => void refetch()}
            className="mt-1 gap-1.5 rounded-[6px] bg-[#0066B2] text-[14px] font-medium text-white hover:bg-[#005494]"
          >
            <ArrowClockwise className="h-4 w-4" />
            <span>Retry</span>
          </Button>
        </div>
      ) : filteredAssets.length === 0 ? (
        searchTerm || typeFilter !== 'ALL' ? (
          <div className="mx-auto flex max-w-[380px] flex-col items-center gap-2.5 py-14 text-center">
            <MagnifyingGlass className="h-7 w-7 text-[#707070]" weight="regular" />
            <div className="text-[18px] font-semibold text-[#1B2021]">
              No media matches
            </div>
            <p className="text-[14px] text-[#696969]">
              Try a different search term, or clear the type filter.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClearFilters}
              className="mt-1 gap-1.5 text-[14px] font-medium text-[#1B2021] hover:bg-[#F4F5F6]"
            >
              <X className="h-4 w-4" />
              <span>Clear filters</span>
            </Button>
          </div>
        ) : (
          <div className="mx-auto flex max-w-[380px] flex-col items-center gap-2.5 py-14 text-center">
            <ImageSquare className="h-7 w-7 text-[#707070]" weight="regular" />
            <div className="text-[18px] font-semibold text-[#1B2021]">No media yet</div>
            <p className="text-[14px] text-[#696969]">
              Upload an image to reuse it across your templates and campaigns.
            </p>
            <Button
              type="button"
              onClick={handleUploadButtonClick}
              className="mt-1 gap-1.5 rounded-[6px] bg-[#0066B2] text-[14px] font-medium text-white hover:bg-[#005494]"
            >
              <UploadSimple className="h-4 w-4" weight="bold" />
              <span>Upload</span>
            </Button>
          </div>
        )
      ) : (
        <div
          id="grid"
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
        >
          {filteredAssets.map((asset) => (
            <MediaCard
              key={asset.id}
              asset={asset}
              onInsert={(target) => setAssetToInsert(target)}
              onDelete={(target) => setAssetToDelete(target)}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal matching prototype */}
      <ConfirmationModal
        open={Boolean(assetToDelete)}
        title={`Delete “${assetToDelete?.name}”?`}
        description="This removes the file from your media library. It is not currently used in any campaign or template content."
        confirmText="Delete file"
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setAssetToDelete(null)}
      />

      {/* Insert Snippet Modal matching prototype */}
      <InsertSnippetModal
        asset={assetToInsert}
        onClose={() => setAssetToInsert(null)}
      />


    </div>
  );
};
